import './TutorialBoard.css'
import './TutorialV2Board.css'
import { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookOpen } from '@fortawesome/free-solid-svg-icons'
import BattleScreen from './v2/BattleScreen'
import KillBar from './v2/KillBar'
import { useTutorial } from '../hooks/useTutorialV2'
import { TUTORIAL_V2_CAPACITIES } from '../config/tutorialV2'
import { TUTORIAL_V2_MACHINE } from '../config/tutorialV2Steps'
import { STRUCTURES_TO_WIN } from '../rules/gameConfig'

// ─── Tutorial v2 ──────────────────────────────────────────────────────────────
// The real BattleScreen, driven by useTutorial (TUTORIAL_V2_DESIGN.md), with
// the instruction panel under it. Shares TutorialBoard's panel styles.
// `tutorial` picks the scripted tutorial it plays: v2's by default, or
// another with the same shape (the progression prototype's v3).

export const TUTORIAL_V2 = {
    machine: TUTORIAL_V2_MACHINE,
    capacities: TUTORIAL_V2_CAPACITIES,
    rounds: 8,
    structuresToWin: STRUCTURES_TO_WIN,
    intro: 'A war between 2 Hives, both hidden from view. Hunt down 3 of your enemy’s structures before they destroy yours.',
    scoreDemo: true,
    endButton: 'Play Hog of War',
}

// The intro card's scoreboard: You and Opp, as in the header, trading kills
// until Opp's third structure falls — then the You side flashes green with a
// burst of confetti (you won) — and starting over.
// A step's text, its paragraphs ("\n\n" in the copy) as blocks, so the gap
// between them can be set (TutorialV2Board.css) rather than a whole empty line.
const Paragraphs = ({ text }) => String(text ?? '').split('\n\n').map((para, i) => (
    <span key={i} className="tb2__para">{para}</span>
))

// How far a step with `eyeArrow` lowers its panel, to clear the arrow.
const EYE_ARROW_ROOM = 34

const SCORE_DEMO = [[0, 0], [0, 1], [1, 1], [1, 2], [2, 2], [2, 3]]   // [you, opp] lost
const CONFETTI_COLORS = ['#f0e040', '#EDA020', '#4A9E58', '#ffffff', '#2ec4b6']
// Fixed scatter (no randomness, so every loop and every render match): each
// piece flies out and down from the You scoreboard's centre.
const CONFETTI = Array.from({ length: 18 }, (_, k) => {
    const a = (k / 18) * Math.PI * 2
    const r = 34 + (k % 3) * 14
    return { dx: Math.round(Math.cos(a) * r * 1.6), dy: Math.round(Math.sin(a) * r + 18), rot: (k * 67) % 360, color: CONFETTI_COLORS[k % CONFETTI_COLORS.length] }
})

function IntroScoreDemo() {
    const [i, setI] = useState(0)
    const [loop, setLoop] = useState(0)
    useEffect(() => {
        const last = i === SCORE_DEMO.length - 1
        const t = setTimeout(() => {
            if (last) setLoop(n => n + 1)
            setI(last ? 0 : i + 1)
        }, i === 0 ? 1200 : last ? 2400 : 900)
        return () => clearTimeout(t)
    }, [i])
    const [you, opp] = SCORE_DEMO[i]
    const won = opp === 3
    return (
        // On the reset frame every slot snaps back with its icon (KillBar's slot
        // fade is delayed to follow a death, which would lag a reset).
        <div className={`tb2__score-demo${i === 0 ? ' tb2__score-demo--reset' : ''}`} aria-hidden="true">
            <div className={`bs__kb-row tb2__score-you${won ? ' tb2__score-you--won' : ''}`}>
                <span className="bs__score-tag bs__score-tag--you">You</span>
                <KillBar lost={you} side="you" silent />
                {won && (
                    <span key={loop} className="tb2__confetti">
                        {CONFETTI.map((p, k) => (
                            <span key={k} className="tb2__confetti-bit"
                                style={{ '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, '--rot': `${p.rot}deg`, background: p.color }} />
                        ))}
                    </span>
                )}
            </div>
            <div className="bs__kb-row bs__kb-row--opp">
                <KillBar lost={opp} side="opp" silent />
                <span className="bs__score-tag bs__score-tag--opp">Opp</span>
            </div>
        </div>
    )
}

// `startAt` opens on that step, the intro card skipped (for reviewing it).
export default function TutorialV2Board({ onComplete, onExit, tutorial = TUTORIAL_V2, startAt = null }) {
    const t = useTutorial(tutorial.machine, { startAt })
    const [started, setStarted] = useState(!!startAt)
    const { step, dispatch } = t

    // While the eye is held, or on a step with `lowerPanel`, the panel slides
    // down to the board's bottom edge, out of the way of their Hive, and back
    // up when it's let go (or the step moves on). It moves a
    // frame after it mounts, so a panel that the press itself brought in
    // (the peek step advances on the press) still visibly slides.
    const [peekHeld, setPeekHeld] = useState(false)
    const panelRef = useRef(null)
    // { id, dy }: how far the panel for step `id` is lowered (another step's
    // panel sits where it mounts, at the top).
    const [lowered, setLowered] = useState({ id: null, dy: 0 })
    const lower = peekHeld || !!step.lowerPanel
    useEffect(() => {
        if (!lower) { setLowered({ id: null, dy: 0 }); return undefined }
        const raf = requestAnimationFrame(() => {
            const panel = panelRef.current
            const board = document.querySelector('[data-testid=hex-board-surface]')
            if (!panel || !board) return
            const p = panel.getBoundingClientRect(), b = board.getBoundingClientRect()
            setLowered({ id: step.id, dy: Math.max(0, b.bottom - 10 - p.bottom) })
        })
        return () => cancelAnimationFrame(raf)
    }, [lower, step.id])
    // A step with `eyeArrow` points a bobbing arrow up at the eye button,
    // and its panel sits a little lower to make room for it.
    const wrapperRef = useRef(null)
    const [eyeAt, setEyeAt] = useState(null)       // the eye button's box in the wrapper
    const showEyeArrow = !!step.eyeArrow && started && !t.isAnimating
    useEffect(() => {
        if (!showEyeArrow) { setEyeAt(null); return undefined }
        const raf = requestAnimationFrame(() => {
            const eye = wrapperRef.current?.querySelector('.bs__eye-btn')
            if (!eye) return
            const e = eye.getBoundingClientRect(), w = wrapperRef.current.getBoundingClientRect()
            setEyeAt({ left: e.left - w.left, top: e.top - w.top, width: e.width, height: e.height })
        })
        return () => cancelAnimationFrame(raf)
    }, [showEyeArrow])
    const panelDy = lowered.id === step.id ? lowered.dy : showEyeArrow ? EYE_ARROW_ROOM : 0

    // Light the step's corridors (0-based for the board) while the player has
    // something to do there.
    // (A pulsePlus step points at the corridors' white plus, not their pills;
    // the other corridors still dim.)
    const highlight = started && !t.isAnimating && step.kind === 'act' && step.highlight ? step.highlight.map(c => c - 1) : null
    // Only a plus still owed a drone pulses: once its corridor has the drones
    // the step asks for, the next plus there sits still.
    const pulseDeploy = step.pulsePlus && highlight
        ? [...new Set(step.deploy ?? [])]
            .filter(c => (t.selfBoard?.[c - 1]?.newArmies || 0) < step.deploy.filter(x => x === c).length)
            .map(c => c - 1)
        : null

    return (
        <div ref={wrapperRef} className="tb__wrapper tb2">
            {/* The arrow and the button's pulsing outline mount together, so
                their animations share one clock. */}
            {showEyeArrow && eyeAt && (
                <>
                    <div className="tb2__eye-ring" style={eyeAt} aria-hidden="true" />
                    <svg className="tb2__eye-arrow" style={{ left: eyeAt.left + eyeAt.width / 2, top: eyeAt.top + eyeAt.height }} viewBox="0 0 30 34" aria-hidden="true">
                        <polygon points="15,0 30,15 20,15 20,34 10,34 10,15 0,15" fill="#ffffff" />
                    </svg>
                </>
            )}
            <BattleScreen
                currentRound={step.round}
                isPaired={true}
                selfBoard={t.selfBoard}
                oppBoard={t.oppBoard}
                selfHf={t.selfHf}
                oppHf={t.oppHf}
                selfLost={t.selfLost}
                oppLost={t.oppLost}
                yieldBalance={t.yieldBalance}
                canDeploy={started && t.canDeploy}
                armiesToDeploy={t.armiesToDeploy}
                isSubmitDisabled={!started || t.isSubmitDisabled}
                pulseSubmit={false}
                message=""
                setMessage={() => {}}
                onAction={t.onAction}
                onSubmit={t.onSubmit}
                onReveal={() => {}}
                isAnimating={t.isAnimating}
                activeClashId={t.activeClashId}
                clashResult={t.clashResult}
                activeClashContext={t.activeClashContext}
                activeClashWinner={t.activeClashWinner}
                activeClashSelfStrength={t.activeClashSelfStrength}
                activeClashOppStrength={t.activeClashOppStrength}
                activeClashOppTargetIndex={t.activeClashOppTargetIndex}
                activeClashSelfTargetIndex={t.activeClashSelfTargetIndex}
                activeClashAbsorbed={t.activeClashAbsorbed}
                activeClashAimS={t.activeClashAimS}
                targetPlots={t.targetPlots}
                targetSubplots={t.targetSubplots}
                onTargetPlot={t.onTargetPlot}
                onStructureKilled={t.onStructureKilled}
                dropZones={t.dropZones}
                requiredDropZoneCount={t.requiredDropZoneCount}
                onDropZone={t.onDropZone}
                placingDropZones={t.placingDropZones}
                resupplyDeliveries={t.resupplyDeliveries}
                aimArrow={started ? t.aimArrow : null}
                gestureHint={started && !t.isAnimating ? step.gesture ?? null : null}
                hiveArrow={started && !t.isAnimating && !!step.hiveArrow}
                selfCellArrow={started && !t.isAnimating && step.cellArrow != null ? { s: step.cellArrow } : null}
                onPeekChange={(held) => { setPeekHeld(held); t.onPeekChange(held) }}
                selfXray={!!step.selfXray}
                hiveOutline={started && !t.isAnimating && !!step.outlineHive}
                corridorSweep={started && !t.isAnimating && step.sweepCorridors ? step.sweepCorridors : false}
                columnSweep={started && !t.isAnimating && !!step.sweepColumns}
                warCapacities={tutorial.capacities}
                structuresToWin={tutorial.structuresToWin}
                highlightCorridors={highlight}
                highlightPills={!step.pulsePlus}
                pulseDeploy={pulseDeploy}
            />

            {/* ── Instruction panel ── */}
            {!t.isAnimating && (!started ? (
                <div key="intro" className="tb__instruction tb__instruction--intro">
                    <FontAwesomeIcon icon={faBookOpen} className="tb__intro-icon" />
                    <span className="tb__intro-title">Tutorial</span>
                    <p className="tb__instruction-text"><Paragraphs text={tutorial.intro} /></p>
                    {tutorial.scoreDemo && <IntroScoreDemo />}
                    <div className="tb__intro-actions">
                        <button className="tb__begin" onClick={() => setStarted(true)}>Begin</button>
                    </div>
                    <div className="tb__intro-actions">
                        <button className="tb__skip" onClick={onExit ?? onComplete}>Exit Tutorial</button>
                    </div>
                </div>
            ) : step.kind === 'end' ? (
                <div key="end" className="tb__instruction tb__instruction--end">
                    <FontAwesomeIcon icon={faBookOpen} className="tb__instruction-icon" />
                    <span className="tb__instruction-title">You won!</span>
                    <p className="tb__instruction-text"><Paragraphs text={t.text} /></p>
                    <button className="tb__play" onClick={onComplete}>{tutorial.endButton}</button>
                </div>
            ) : (
                <div key={step.id} ref={panelRef} className="tb__instruction"
                    style={{ translate: `0 ${panelDy}px`, transition: 'translate 0.4s cubic-bezier(0.3, 0, 0.2, 1)' }}>
                    <div className="tb__skip-row">
                        {t.canGoBack && <button className="tb__skip" onClick={() => dispatch({ type: 'back' })}>Back</button>}
                        <button className="tb__skip" onClick={onExit ?? onComplete}>Exit Tutorial</button>
                    </div>
                    <div className="tb__instruction-header">
                        <span className="tb__instruction-title">
                            <FontAwesomeIcon icon={faBookOpen} className="tb__instruction-icon" />
                            Round {step.round} of {tutorial.rounds}
                        </span>
                    </div>
                    <p className="tb__instruction-text"><Paragraphs text={t.text} /></p>
                    {t.tip && <p className="tb2__tip" role="status">{t.tip}</p>}
                    {step.kind === 'next' && (
                        <button className="tb__next" onClick={() => dispatch({ type: 'next' })}>Next</button>
                    )}
                </div>
            ))}

        </div>
    )
}
